import { useState, useCallback, useRef, useEffect } from "react";
import {
  FileEntity,
  FileStatus,
} from "@quantizr-engineering/common/types/file";
import DropzoneOverlay from "./DropzoneOverlay";
import { FaTrash } from "react-icons/fa";
import GradientButton from "./GradientButton";
import { toast } from "sonner";
import { ContractType, Show } from "@quantizr-engineering/common/types/show";
import { Formik, Form } from "formik";
import * as Yup from "yup";
import { useFileService } from "../api/fileService.ts";
import { useShowService } from "../api/showService.ts";
import { useRunService } from "../api/runService.ts";

type ShowOfferFileModalProps = {
  onClose: (show: Show | null) => void;
  initialFile?: Partial<FileEntity>;
};

const ShowOfferFileModal: React.FC<ShowOfferFileModalProps> = ({
  onClose,
  initialFile,
}) => {
  const [fileUpload, setFileUpload] = useState<File | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { fileEntitiesLoading } = useFileService();
  const { uploadShowOfferStreamlined } = useShowService();
  const { getRuns } = useRunService();
  const [isUploading, setIsUploading] = useState(false);

  const [ready, setReady] = useState(false);

  useEffect(() => {
    if (!!fileEntitiesLoading) return;
    setReady(true);
  }, [fileEntitiesLoading]);

  const handleFileSelect = useCallback((files: File[]) => {
    if (files.length > 0) {
      setFileUpload(files[0]); // Only take the first file
    }
  }, []);

  const validationSchema = Yup.object().shape({
    file: Yup.object()
      .shape({
        name: Yup.string().required("File name is required"),
        filename: Yup.string().required("Filename is required"),
      })
      .required("File is required"),
  });

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files && event.target.files.length > 0) {
      handleFileSelect([event.target.files[0]]);
    }
  };

  const handleBoxClick = () => {
    fileInputRef.current?.click();
  };

  const handleRemoveFile = () => {
    setFileUpload(null);
  };

  if (!ready) {
    return (
      <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50">
        <div className="fixed inset-0">
          <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2">
            <div className="bg-gray-100 p-4 rounded-lg">
              <h3 className="text-lg font-semibold">Loading...</h3>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50">
      <div className="fixed inset-0" onClick={() => onClose(null)}>
        <div
          className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-full max-w-2xl h-[50vh]"
          onClick={(e) => e.stopPropagation()}
        >
          <button
            onClick={() => onClose(null)}
            className="absolute top-2 right-4 text-black font-bold hover:text-gray-600 z-10"
          >
            X
          </button>

          <div className="bg-gray-100 p-4 h-full pb-16 rounded-lg">
            <h3 className="text-lg font-semibold mb-3">Analyze Show Offer</h3>
            <div className="bg-white shadow-md rounded-lg overflow-hidden h-full flex flex-col">
              <Formik
                initialValues={{
                  file: fileUpload
                    ? {
                        name: fileUpload.name,
                        filename: fileUpload.name,
                        mimeType: fileUpload.type,
                        contractType: ContractType.LIVE_MUSIC_AGREEMENT,
                        status: FileStatus.PENDING,
                        uploadedAt: new Date(),
                      }
                    : initialFile
                    ? {
                        ...initialFile,
                        filename: initialFile.name,
                        contractType: ContractType.LIVE_MUSIC_AGREEMENT,
                        status: FileStatus.PENDING,
                        uploadedAt: new Date(),
                      }
                    : null,
                }}
                enableReinitialize={true}
                validationSchema={validationSchema}
                onSubmit={async (values, { setSubmitting }) => {
                  if (!fileUpload || !values.file) {
                    toast.error("Please select a file to upload");
                    return;
                  }
                  setIsUploading(true);
                  try {
                    const res = await uploadShowOfferStreamlined(fileUpload);
                    await getRuns();
                    onClose(res.data);
                  } catch (error) {
                    toast.error("Upload failed");
                  } finally {
                    setSubmitting(false);
                    setIsUploading(false);
                  }
                }}
              >
                {({ values, handleSubmit, setFieldValue }) => {
                  return (
                    <Form
                      onSubmit={handleSubmit}
                      className="flex flex-col h-full"
                    >
                      <div className="flex-1 flex flex-col pb-4 min-h-0">
                        {!fileUpload && (
                          <div
                            onClick={handleBoxClick}
                            className="mb-4 p-4 m-4 border-2 border-dashed border-gray-200 text-center cursor-pointer hover:bg-gray-50 transition-colors duration-200"
                          >
                            <p className="text-gray-500 text-lg mb-4">
                              Click here or drag a file to upload show offer
                            </p>
                            <p className="text-sm text-gray-400 mt-4">
                              Only one file allowed
                            </p>
                          </div>
                        )}

                        <DropzoneOverlay
                          onDrop={(files) => {
                            handleFileSelect(files);
                            if (files.length > 0) {
                              const file = files[0];
                              setFieldValue("file", {
                                name: file.name,
                                filename: file.name,
                                mimeType: file.type,
                                contractType: ContractType.LIVE_MUSIC_AGREEMENT,
                                status: FileStatus.PENDING,
                                uploadedAt: new Date(),
                              });
                            }
                          }}
                        />

                        <input
                          ref={fileInputRef}
                          type="file"
                          className="hidden"
                          onChange={(e) => {
                            handleInputChange(e);
                            if (e.target.files && e.target.files.length > 0) {
                              const file = e.target.files[0];
                              setFieldValue("file", {
                                name: file.name,
                                filename: file.name,
                                mimeType: file.type,
                                contractType: ContractType.LIVE_MUSIC_AGREEMENT,
                                status: FileStatus.PENDING,
                                uploadedAt: new Date(),
                              });
                            }
                          }}
                        />

                        {fileUpload && values.file && (
                          <div className="flex-1 overflow-y-auto">
                            <div className="px-4 py-4 border-b bg-gray-50">
                              <h4 className="font-medium">Selected File</h4>
                            </div>

                            <div className="flex items-center justify-between p-4 border-b">
                              <div className="flex-1">
                                <p className="font-medium">{fileUpload.name}</p>
                                <p className="text-sm text-gray-500">
                                  {(fileUpload.size / 1024 / 1024).toFixed(2)}{" "}
                                  MB
                                </p>
                              </div>
                              <button
                                type="button"
                                onClick={handleRemoveFile}
                                className="text-gray-500 hover:text-red-500 transition-colors ml-4"
                              >
                                <FaTrash className="h-5 w-5" />
                              </button>
                            </div>
                          </div>
                        )}
                      </div>

                      {/* Buttons always at bottom */}
                      <div className="p-4 border-t bg-gray-50 flex justify-end space-x-4">
                        <button
                          type="button"
                          onClick={() => onClose(null)}
                          className="px-4 py-2 text-gray-600 hover:text-gray-800"
                        >
                          Cancel
                        </button>
                        {isUploading ? (
                          <GradientButton
                            text="Uploading..."
                            type="submit"
                            disabled={true}
                          />
                        ) : (
                          <GradientButton
                            text="Analyze"
                            type="submit"
                            disabled={!fileUpload}
                            className="min-w-[130px]"
                          />
                        )}
                      </div>
                    </Form>
                  );
                }}
              </Formik>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ShowOfferFileModal;
